import { numeralSystem } from '@aj/tokens';

import { bn } from './bn.js';
import { en } from './en.js';
import type { Dictionary, MessageKey } from './en.js';

export type { MessageKey, Dictionary };
export type Locale = 'en' | 'bn';

/**
 * Locales the app actually ships with.
 *
 * Bangla is written, typed and compile-checked against the English dictionary
 * — it just isn't switched on yet. Add `'bn'` here and it lights up: the
 * language switcher maps over this array, `detectLocale` filters against it,
 * and the Bengali font face is already declared with its own unicode-range.
 *
 * Keeping bn.ts in the build (rather than deleting it) means it cannot rot:
 * every new key breaks typecheck until Bangla has it too.
 */
export const locales: Locale[] = ['en'];
export const localeNames: Record<Locale, string> = { en: 'English', bn: 'বাংলা' };

const dictionaries: Record<Locale, Dictionary> = { en: en as Dictionary, bn };

/** Intl locale tags — distinct from our app locale ids. */
const intlTag: Record<Locale, string> = { en: 'en-GB', bn: 'bn-BD' };

export type Params = Record<string, string | number>;

/**
 * A whole i18n runtime in ~60 lines, versus ~40KB for i18next. Plurals and
 * formatting come from `Intl`, which every target platform already ships.
 */
export function createI18n(initial: Locale = 'en') {
  let locale: Locale = initial;
  const listeners = new Set<() => void>();

  let plural = new Intl.PluralRules(intlTag[locale]);
  let number = new Intl.NumberFormat(intlTag[locale], { numberingSystem: numeralSystem });

  const rebuild = () => {
    plural = new Intl.PluralRules(intlTag[locale]);
    number = new Intl.NumberFormat(intlTag[locale], { numberingSystem: numeralSystem });
  };

  const interpolate = (template: string, params?: Params) =>
    params
      ? template.replace(/\{(\w+)\}/g, (match, key: string) => {
          const value = params[key];
          if (value === undefined) return match;
          return typeof value === 'number' ? number.format(value) : value;
        })
      : template;

  function t(key: MessageKey, params?: Params): string {
    const entry = dictionaries[locale][key] ?? dictionaries.en[key];
    if (typeof entry === 'string') return interpolate(entry, params);

    // Plural form — `count` selects the branch.
    const count = typeof params?.count === 'number' ? params.count : 0;
    const rule = plural.select(count) as keyof typeof entry;
    return interpolate(entry[rule] ?? entry.other, params);
  }

  return {
    t,
    get locale() {
      return locale;
    },
    setLocale(next: Locale) {
      if (next === locale) return;
      locale = next;
      rebuild();
      listeners.forEach((fn) => fn());
    },
    subscribe(fn: () => void) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },

    formatNumber: (n: number) => number.format(n),

    formatDate: (ts: number, options?: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat(intlTag[locale], {
        numberingSystem: numeralSystem,
        ...options,
      }).format(ts),

    /** "1h 25m" / "1ঘ 25মি" — the spec's canonical study-time rendering. */
    formatDuration(totalMinutes: number): string {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = Math.round(totalMinutes % 60);
      if (hours === 0) return t('duration.minutes', { minutes });
      if (minutes === 0) return t('duration.hours', { hours });
      return t('duration.hoursMinutes', { hours, minutes });
    },

    /**
     * "ch.3, ch.4 and ch.7" — joined the way the reader's language joins things.
     *
     * `Intl.ListFormat` because the separator and the final conjunction differ
     * by language, and hand-joining with ", " and " and " would be English
     * grammar wearing a translation.
     */
    formatList(items: readonly string[]): string {
      return new Intl.ListFormat(intlTag[locale], {
        style: 'long',
        type: 'conjunction',
      }).format(items);
    },
  };
}

export type I18n = ReturnType<typeof createI18n>;

/** Pick a starting locale from the device, restricted to what we ship. */
export function detectLocale(candidates: readonly string[]): Locale {
  for (const tag of candidates) {
    const lower = tag.toLowerCase();
    const match: Locale | null = lower.startsWith('bn')
      ? 'bn'
      : lower.startsWith('en')
        ? 'en'
        : null;
    if (match && locales.includes(match)) return match;
  }
  return locales[0] ?? 'en';
}
